import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Save, Plus, Trash2, Download, CheckCircle, AlertCircle, Loader2, WifiOff, RefreshCw, CloudOff, Cloud, Printer } from "lucide-react";
import {
  initOfflineDB,
  saveOfflineInvoice,
  getPendingInvoices,
  markInvoiceSynced,
  getPendingCount,
  isOnline,
  addConnectivityListener
} from "../lib/offlineStorage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Minimal inline styles for instant loading (no Tailwind processing needed)
const styles = {
  container: {
    maxWidth: "1000px",
    margin: "0 auto",
    padding: "16px",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
    paddingBottom: "12px",
    borderBottom: "2px solid #e2e8f0"
  },
  title: {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#1e293b",
    margin: 0
  },
  badge: {
    background: "#dbeafe",
    color: "#1d4ed8",
    padding: "4px 12px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "600"
  },
  section: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    padding: "16px",
    marginBottom: "16px"
  },
  sectionTitle: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#475569",
    marginBottom: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.5px"
  },
  grid2: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px"
  },
  grid3: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "12px"
  },
  label: {
    display: "block",
    fontSize: "12px",
    fontWeight: "500",
    color: "#64748b",
    marginBottom: "4px"
  },
  input: {
    width: "100%",
    padding: "8px 12px",
    border: "1px solid #e2e8f0",
    borderRadius: "6px",
    fontSize: "14px",
    boxSizing: "border-box"
  },
  select: {
    width: "100%",
    padding: "8px 12px",
    border: "1px solid #e2e8f0",
    borderRadius: "6px",
    fontSize: "14px",
    background: "#fff",
    boxSizing: "border-box"
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "14px"
  },
  th: {
    textAlign: "left",
    padding: "8px",
    borderBottom: "2px solid #e2e8f0",
    fontWeight: "600",
    color: "#475569",
    fontSize: "12px"
  },
  td: {
    padding: "8px",
    borderBottom: "1px solid #f1f5f9"
  },
  smallInput: {
    width: "100%",
    padding: "6px 8px",
    border: "1px solid #e2e8f0",
    borderRadius: "4px",
    fontSize: "13px",
    boxSizing: "border-box"
  },
  btnPrimary: {
    background: "#2563eb",
    color: "#fff",
    border: "none",
    padding: "10px 20px",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px"
  },
  btnSecondary: {
    background: "#f1f5f9",
    color: "#475569",
    border: "1px solid #e2e8f0",
    padding: "10px 20px",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px"
  },
  btnDanger: {
    background: "#fee2e2",
    color: "#dc2626",
    border: "none",
    padding: "6px",
    borderRadius: "4px",
    cursor: "pointer"
  },
  btnSmall: {
    background: "#f1f5f9",
    color: "#475569",
    border: "1px solid #e2e8f0",
    padding: "6px 12px",
    borderRadius: "4px",
    fontSize: "12px",
    cursor: "pointer"
  },
  totalsBox: {
    background: "#f8fafc",
    padding: "16px",
    borderRadius: "8px",
    marginTop: "16px"
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "6px 0",
    fontSize: "14px"
  },
  totalFinal: {
    display: "flex",
    justifyContent: "space-between",
    padding: "12px 0",
    fontSize: "18px",
    fontWeight: "bold",
    borderTop: "2px solid #e2e8f0",
    marginTop: "8px"
  },
  alert: {
    padding: "12px 16px",
    borderRadius: "8px",
    marginBottom: "16px",
    display: "flex",
    alignItems: "center",
    gap: "12px"
  },
  alertSuccess: {
    background: "#dcfce7",
    color: "#166534"
  },
  alertError: {
    background: "#fee2e2",
    color: "#dc2626"
  },
  footer: {
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
    marginTop: "20px",
    paddingTop: "16px",
    borderTop: "1px solid #e2e8f0"
  },
  link: {
    color: "#2563eb",
    textDecoration: "none",
    fontSize: "14px"
  },
  offlineBanner: {
    background: "#fef3c7",
    color: "#92400e",
    padding: "10px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    marginBottom: "16px",
    borderRadius: "8px",
    fontSize: "14px"
  },
  onlineBanner: {
    background: "#dcfce7",
    color: "#166534",
    padding: "10px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    marginBottom: "16px",
    borderRadius: "8px",
    fontSize: "14px"
  },
  syncButton: {
    background: "#059669",
    color: "#fff",
    border: "none",
    padding: "6px 12px",
    borderRadius: "4px",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px"
  },
  pendingBadge: {
    background: "#f59e0b",
    color: "#fff",
    padding: "2px 8px",
    borderRadius: "10px",
    fontSize: "11px",
    fontWeight: "600"
  },
  offlineTag: {
    background: "#fbbf24",
    color: "#78350f",
    padding: "4px 12px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "600"
  }
};

export default function LiteInvoice() {
  const [saving, setSaving] = useState(false);
  const [savedInvoice, setSavedInvoice] = useState(null);
  const [error, setError] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  
  // Offline state
  const [online, setOnline] = useState(isOnline());
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);
  const [savedOffline, setSavedOffline] = useState(false);
  
  // Invoice data
  const [invoice, setInvoice] = useState({
    customer_id: "",
    customer_name: "",
    customer_phone: "",
    customer_address: "",
    status: "estimate",
    tax_rate: 8.75,
    road_service_fee: 0,
    storage_fee: 0,
    vehicle_info: {
      truck_number: "",
      trailer_number: "",
      make: "",
      type_model: "",
      year: "",
      serial_vin: "",
      license_number: "",
      odometer: "",
      written_by: ""
    },
    items: [{ 
      parts_qty: 0, 
      parts_description: "", 
      parts_price: 0, 
      labor_qty: 0, 
      labor_description: "", 
      labor_price: 0 
    }]
  });

  // Load customers in background (non-blocking)
  useEffect(() => {
    if (online) {
      axios.get(`${API}/customers`)
        .then(res => setCustomers(res.data || []))
        .catch(() => setCustomers([]))
        .finally(() => setLoadingCustomers(false));
    } else {
      setLoadingCustomers(false);
    }
  }, [online]);

  // Initialize offline database and connectivity listener
  useEffect(() => {
    initOfflineDB().catch(console.error);
    
    // Update pending count
    const updatePendingCount = async () => {
      try {
        const count = await getPendingCount();
        setPendingCount(count);
      } catch (err) {
        console.error('Failed to get pending count:', err);
      }
    };
    
    updatePendingCount();
    
    // Listen for connectivity changes
    const cleanup = addConnectivityListener((isNowOnline) => {
      setOnline(isNowOnline);
      if (isNowOnline) {
        // Auto-sync when back online
        syncPendingInvoices();
      }
    });
    
    return cleanup;
  }, []);

  // Sync pending invoices to server
  const syncPendingInvoices = async () => {
    if (!online || syncing) return;
    
    setSyncing(true);
    setSyncMessage(null);
    
    try {
      const pending = await getPendingInvoices();
      
      if (pending.length === 0) {
        setSyncMessage({ type: 'info', text: 'No pending invoices to sync' });
        setSyncing(false);
        return;
      }
      
      let synced = 0;
      let failed = 0;
      
      for (const offlineInvoice of pending) {
        try {
          // Prepare invoice data for server
          const invoiceData = {
            customer_id: offlineInvoice.customer_id,
            customer_name: offlineInvoice.customer_name,
            customer_phone: offlineInvoice.customer_phone,
            customer_address: offlineInvoice.customer_address,
            status: offlineInvoice.status,
            tax_rate: parseFloat(offlineInvoice.tax_rate) || 0,
            road_service_fee: parseFloat(offlineInvoice.road_service_fee) || 0,
            storage_fee: parseFloat(offlineInvoice.storage_fee) || 0,
            vehicle_info: offlineInvoice.vehicle_info,
            items: (offlineInvoice.items || []).map(item => ({
              ...item,
              parts_qty: parseFloat(item.parts_qty) || 0,
              parts_price: parseFloat(item.parts_price) || 0,
              labor_qty: parseFloat(item.labor_qty) || 0,
              labor_price: parseFloat(item.labor_price) || 0
            }))
          };
          
          const response = await axios.post(`${API}/invoices`, invoiceData);
          await markInvoiceSynced(offlineInvoice.localId, response.data);
          synced++;
        } catch (err) {
          console.error('Failed to sync invoice:', err);
          failed++;
        }
      }
      
      // Update pending count
      const newCount = await getPendingCount();
      setPendingCount(newCount);
      
      if (failed === 0) {
        setSyncMessage({ type: 'success', text: `${synced} invoice${synced !== 1 ? 's' : ''} synced successfully!` });
      } else {
        setSyncMessage({ type: 'warning', text: `${synced} synced, ${failed} failed. Will retry.` });
      }
    } catch (err) {
      console.error('Sync failed:', err);
      setSyncMessage({ type: 'error', text: 'Sync failed. Will retry when online.' });
    } finally {
      setSyncing(false);
    }
  };

  // Calculate totals
  const calculateTotals = useCallback(() => {
    const items = invoice.items || [];
    const partsTotal = items.reduce((sum, item) => 
      sum + (parseFloat(item.parts_qty) || 0) * (parseFloat(item.parts_price) || 0), 0);
    const laborTotal = items.reduce((sum, item) => 
      sum + (parseFloat(item.labor_qty) || 0) * (parseFloat(item.labor_price) || 0), 0);
    const roadFee = parseFloat(invoice.road_service_fee) || 0;
    const storageFee = parseFloat(invoice.storage_fee) || 0;
    const subtotal = partsTotal + laborTotal + roadFee + storageFee;
    const taxAmount = partsTotal * ((parseFloat(invoice.tax_rate) || 0) / 100);
    const total = subtotal + taxAmount;
    
    return { partsTotal, laborTotal, subtotal, taxAmount, total };
  }, [invoice]);

  const totals = calculateTotals();

  // Handle customer selection
  const handleCustomerSelect = (customerId) => {
    if (!customerId) {
      setInvoice(prev => ({ ...prev, customer_id: "", customer_name: "", customer_phone: "", customer_address: "" }));
      return;
    }
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      setInvoice(prev => ({
        ...prev,
        customer_id: customer.id,
        customer_name: customer.name || "",
        customer_phone: customer.phone || "",
        customer_address: customer.address || ""
      }));
    }
  };

  // Handle item changes
  const updateItem = (index, field, value) => {
    const newItems = [...invoice.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setInvoice(prev => ({ ...prev, items: newItems }));
  };

  const addItem = () => {
    setInvoice(prev => ({
      ...prev,
      items: [...prev.items, { 
        parts_qty: 0, 
        parts_description: "", 
        parts_price: 0, 
        labor_qty: 0, 
        labor_description: "", 
        labor_price: 0 
      }]
    }));
  };

  const removeItem = (index) => {
    if (invoice.items.length > 1) {
      setInvoice(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
    }
  };

  // Save invoice (online or offline)
  const saveInvoice = async () => {
    setSaving(true);
    setError(null);
    setSavedOffline(false);
    
    const invoiceData = {
      ...invoice,
      items: invoice.items.map(item => ({
        ...item,
        parts_qty: parseFloat(item.parts_qty) || 0,
        parts_price: parseFloat(item.parts_price) || 0,
        labor_qty: parseFloat(item.labor_qty) || 0,
        labor_price: parseFloat(item.labor_price) || 0
      })),
      road_service_fee: parseFloat(invoice.road_service_fee) || 0,
      storage_fee: parseFloat(invoice.storage_fee) || 0,
      tax_rate: parseFloat(invoice.tax_rate) || 0
    };
    
    // Try online save first
    if (online) {
      try {
        const response = await axios.post(`${API}/invoices`, invoiceData);
        setSavedInvoice(response.data);
        setSaving(false);
        return;
      } catch (err) {
        // If online save fails, fall through to offline save
        console.log('Online save failed, saving offline:', err.message);
      }
    }
    
    // Save offline
    try {
      const offlineResult = await saveOfflineInvoice(invoiceData);
      setSavedInvoice(offlineResult);
      setSavedOffline(true);
      
      // Update pending count
      const count = await getPendingCount();
      setPendingCount(count);
    } catch (err) {
      setError("Failed to save invoice offline");
    } finally {
      setSaving(false);
    }
  };

  // Download PDF
  const downloadPDF = async () => {
    if (!savedInvoice?.id) {
      setError("No invoice ID available for PDF download");
      return;
    }
    
    try {
      const response = await axios.get(`${API}/invoices/${savedInvoice.id}/pdf`, {
        responseType: 'blob',
        timeout: 30000
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${savedInvoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (err) {
      console.error("PDF download error:", err);
      setError("Failed to download PDF. Please try again.");
    }
  };

  // Create new invoice
  const createNew = () => {
    setSavedInvoice(null);
    setSavedOffline(false);
    setInvoice({
      customer_id: "",
      customer_name: "",
      customer_phone: "",
      customer_address: "",
      status: "estimate",
      tax_rate: 8.75,
      road_service_fee: 0,
      storage_fee: 0,
      vehicle_info: {
        truck_number: "",
        trailer_number: "",
        make: "",
        type_model: "",
        year: "",
        serial_vin: "",
        license_number: "",
        odometer: "",
        written_by: ""
      },
      items: [{ 
        parts_qty: 0, 
        parts_description: "", 
        parts_price: 0, 
        labor_qty: 0, 
        labor_description: "", 
        labor_price: 0 
      }]
    });
  };

  // Success state
  if (savedInvoice) {
    return (
      <div style={styles.container}>
        {/* Offline banner in success state */}
        {!online && (
          <div style={styles.offlineBanner}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <WifiOff size={18} />
              <span>You're offline</span>
            </div>
          </div>
        )}
        
        <div style={{ ...styles.alert, ...(savedOffline ? { background: "#fef3c7", color: "#92400e" } : styles.alertSuccess) }}>
          {savedOffline ? <CloudOff size={24} /> : <CheckCircle size={24} />}
          <div>
            <strong>{savedOffline ? "Invoice Saved Offline!" : "Invoice Saved Successfully!"}</strong>
            <div style={{ fontSize: "14px", marginTop: "4px" }}>
              {savedOffline 
                ? `Invoice ${savedInvoice.invoice_number} saved locally. Will sync when online.`
                : `Invoice #${savedInvoice.invoice_number} has been created`
              }
            </div>
          </div>
        </div>
        
        <div style={{ ...styles.section, textAlign: "center", padding: "40px" }}>
          <h2 style={{ margin: "0 0 8px 0", color: "#1e293b" }}>{savedInvoice.invoice_number}</h2>
          {savedOffline && (
            <span style={styles.offlineTag}>Pending Sync</span>
          )}
          <p style={{ color: "#64748b", margin: "12px 0 24px 0" }}>
            {savedInvoice.customer_name || "No customer"} - ${totals.total.toFixed(2)}
          </p>
          
          <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
            {!savedOffline && (
              <>
                <button 
                  style={{ ...styles.btnPrimary, background: "#059669" }} 
                  onClick={() => window.open(`/print/${savedInvoice.id}`, '_blank')}
                >
                  <Printer size={18} /> Print
                </button>
                <button style={styles.btnPrimary} onClick={downloadPDF}>
                  <Download size={18} /> Download PDF
                </button>
              </>
            )}
            {savedOffline && online && (
              <button style={styles.syncButton} onClick={syncPendingInvoices} disabled={syncing}>
                {syncing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                Sync Now
              </button>
            )}
            <button style={styles.btnSecondary} onClick={createNew}>
              <Plus size={18} /> Create Another
            </button>
          </div>
          
          {savedOffline && (
            <p style={{ fontSize: "13px", color: "#78350f", marginTop: "16px" }}>
              PDF download available after sync completes
            </p>
          )}
          
          <div style={{ marginTop: "24px", paddingTop: "24px", borderTop: "1px solid #e2e8f0" }}>
            <a href="/" style={styles.link}>Go to Full App</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Offline/Online Status Banner */}
      {!online && (
        <div style={styles.offlineBanner}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <WifiOff size={18} />
            <span><strong>Offline Mode</strong> - Invoices will be saved locally and sync when back online</span>
          </div>
          {pendingCount > 0 && (
            <span style={styles.pendingBadge}>{pendingCount} pending</span>
          )}
        </div>
      )}
      
      {online && pendingCount > 0 && (
        <div style={styles.onlineBanner}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Cloud size={18} />
            <span><strong>{pendingCount} invoice{pendingCount !== 1 ? 's' : ''}</strong> waiting to sync</span>
          </div>
          <button 
            style={styles.syncButton} 
            onClick={syncPendingInvoices}
            disabled={syncing}
          >
            {syncing ? <Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} /> : <RefreshCw size={14} />}
            {syncing ? "Syncing..." : "Sync Now"}
          </button>
        </div>
      )}
      
      {syncMessage && (
        <div style={{ 
          ...styles.alert, 
          ...(syncMessage.type === 'success' ? styles.alertSuccess : 
              syncMessage.type === 'error' ? styles.alertError : 
              { background: "#dbeafe", color: "#1e40af" })
        }}>
          {syncMessage.type === 'success' ? <CheckCircle size={18} /> : 
           syncMessage.type === 'error' ? <AlertCircle size={18} /> : 
           <Cloud size={18} />}
          {syncMessage.text}
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Quick Invoice</h1>
          <p style={{ margin: "4px 0 0 0", color: "#64748b", fontSize: "14px" }}>
            Lite Mode - Fast invoice creation {!online && "(Offline)"}
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {!online && <span style={styles.offlineTag}>OFFLINE</span>}
          <span style={styles.badge}>LITE</span>
        </div>
      </div>

      {error && (
        <div style={{ ...styles.alert, ...styles.alertError }}>
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      {/* Customer Section */}
      <div style={styles.section}>
        <div style={styles.sectionTitle}>Customer Information</div>
        <div style={styles.grid2}>
          <div>
            <label style={styles.label}>Select Customer</label>
            <select 
              style={styles.select}
              value={invoice.customer_id}
              onChange={(e) => handleCustomerSelect(e.target.value)}
            >
              <option value="">{loadingCustomers ? "Loading..." : "-- New/Walk-in Customer --"}</option>
              {customers.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={styles.label}>Customer Name</label>
            <input 
              style={styles.input}
              value={invoice.customer_name}
              onChange={(e) => setInvoice(prev => ({ ...prev, customer_name: e.target.value }))}
              placeholder="Enter name"
            />
          </div>
          <div>
            <label style={styles.label}>Phone</label>
            <input 
              style={styles.input}
              value={invoice.customer_phone}
              onChange={(e) => setInvoice(prev => ({ ...prev, customer_phone: e.target.value }))}
              placeholder="Phone number"
            />
          </div>
          <div>
            <label style={styles.label}>Address</label>
            <input 
              style={styles.input}
              value={invoice.customer_address}
              onChange={(e) => setInvoice(prev => ({ ...prev, customer_address: e.target.value }))}
              placeholder="Address"
            />
          </div>
        </div>
      </div>

      {/* Vehicle Section */}
      <div style={styles.section}>
        <div style={styles.sectionTitle}>Vehicle / Job Details</div>
        <div style={styles.grid3}>
          <div>
            <label style={styles.label}>Truck #</label>
            <input 
              style={styles.input}
              value={invoice.vehicle_info.truck_number}
              onChange={(e) => setInvoice(prev => ({ 
                ...prev, 
                vehicle_info: { ...prev.vehicle_info, truck_number: e.target.value }
              }))}
            />
          </div>
          <div>
            <label style={styles.label}>Trailer #</label>
            <input 
              style={styles.input}
              value={invoice.vehicle_info.trailer_number}
              onChange={(e) => setInvoice(prev => ({ 
                ...prev, 
                vehicle_info: { ...prev.vehicle_info, trailer_number: e.target.value }
              }))}
            />
          </div>
          <div>
            <label style={styles.label}>Make</label>
            <input 
              style={styles.input}
              value={invoice.vehicle_info.make}
              onChange={(e) => setInvoice(prev => ({ 
                ...prev, 
                vehicle_info: { ...prev.vehicle_info, make: e.target.value }
              }))}
            />
          </div>
          <div>
            <label style={styles.label}>Model</label>
            <input 
              style={styles.input}
              value={invoice.vehicle_info.type_model}
              onChange={(e) => setInvoice(prev => ({ 
                ...prev, 
                vehicle_info: { ...prev.vehicle_info, type_model: e.target.value }
              }))}
            />
          </div>
          <div>
            <label style={styles.label}>Year</label>
            <input 
              style={styles.input}
              value={invoice.vehicle_info.year}
              onChange={(e) => setInvoice(prev => ({ 
                ...prev, 
                vehicle_info: { ...prev.vehicle_info, year: e.target.value }
              }))}
            />
          </div>
          <div>
            <label style={styles.label}>VIN #</label>
            <input 
              style={styles.input}
              value={invoice.vehicle_info.serial_vin}
              onChange={(e) => setInvoice(prev => ({ 
                ...prev, 
                vehicle_info: { ...prev.vehicle_info, serial_vin: e.target.value }
              }))}
            />
          </div>
          <div>
            <label style={styles.label}>License #</label>
            <input 
              style={styles.input}
              value={invoice.vehicle_info.license_number}
              onChange={(e) => setInvoice(prev => ({ 
                ...prev, 
                vehicle_info: { ...prev.vehicle_info, license_number: e.target.value }
              }))}
            />
          </div>
          <div>
            <label style={styles.label}>Odometer</label>
            <input 
              style={styles.input}
              value={invoice.vehicle_info.odometer}
              onChange={(e) => setInvoice(prev => ({ 
                ...prev, 
                vehicle_info: { ...prev.vehicle_info, odometer: e.target.value }
              }))}
            />
          </div>
          <div>
            <label style={styles.label}>Written By</label>
            <input 
              style={styles.input}
              value={invoice.vehicle_info.written_by}
              onChange={(e) => setInvoice(prev => ({ 
                ...prev, 
                vehicle_info: { ...prev.vehicle_info, written_by: e.target.value }
              }))}
            />
          </div>
        </div>
      </div>

      {/* Parts & Labor Tables - Side by Side */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
        {/* Parts Table - Left Side */}
        <div style={{ ...styles.section, marginBottom: 0 }}>
          <div style={{ background: "#2563eb", color: "#fff", margin: "-16px -16px 12px -16px", padding: "10px 16px", fontWeight: "600", borderRadius: "8px 8px 0 0" }}>
            PARTS
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "8px" }}>
            <button style={styles.btnSmall} onClick={addItem}>
              <Plus size={14} /> Add Row
            </button>
          </div>
          <table style={styles.table}>
            <thead>
              <tr style={{ background: "#dbeafe" }}>
                <th style={{ ...styles.th, width: "15%" }}>Qty</th>
                <th style={{ ...styles.th, width: "45%" }}>Description</th>
                <th style={{ ...styles.th, width: "20%" }}>Price</th>
                <th style={{ ...styles.th, width: "20%", textAlign: "right" }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items.map((item, index) => {
                const partsTotal = (parseFloat(item.parts_qty) || 0) * (parseFloat(item.parts_price) || 0);
                return (
                  <tr key={`parts-${index}`}>
                    <td style={styles.td}>
                      <input 
                        style={styles.smallInput}
                        type="number"
                        min="0"
                        value={item.parts_qty || ""}
                        onChange={(e) => updateItem(index, "parts_qty", e.target.value)}
                      />
                    </td>
                    <td style={styles.td}>
                      <input 
                        style={styles.smallInput}
                        value={item.parts_description || ""}
                        onChange={(e) => updateItem(index, "parts_description", e.target.value)}
                        placeholder="Part description"
                      />
                    </td>
                    <td style={styles.td}>
                      <input 
                        style={styles.smallInput}
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.parts_price || ""}
                        onChange={(e) => updateItem(index, "parts_price", e.target.value)}
                        placeholder="0.00"
                      />
                    </td>
                    <td style={{ ...styles.td, textAlign: "right", fontWeight: "500", fontFamily: "monospace" }}>
                      ${partsTotal.toFixed(2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ background: "#dbeafe" }}>
                <td colSpan="2" style={{ ...styles.td, fontWeight: "600", fontSize: "12px" }}>
                  Tax: ${totals.taxAmount.toFixed(2)}
                </td>
                <td style={{ ...styles.td, textAlign: "right", fontWeight: "600", fontSize: "12px" }}>Total:</td>
                <td style={{ ...styles.td, textAlign: "right", fontWeight: "700", fontFamily: "monospace" }}>
                  ${totals.partsTotal.toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Labor Table - Right Side */}
        <div style={{ ...styles.section, marginBottom: 0 }}>
          <div style={{ background: "#475569", color: "#fff", margin: "-16px -16px 12px -16px", padding: "10px 16px", fontWeight: "600", borderRadius: "8px 8px 0 0" }}>
            LABOR
          </div>
          <div style={{ height: "32px" }}></div> {/* Spacer to align with Parts table */}
          <table style={styles.table}>
            <thead>
              <tr style={{ background: "#f1f5f9" }}>
                <th style={{ ...styles.th, width: "15%" }}>Qty</th>
                <th style={{ ...styles.th, width: "45%" }}>Description</th>
                <th style={{ ...styles.th, width: "20%" }}>Price</th>
                <th style={{ ...styles.th, width: "20%", textAlign: "right" }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items.map((item, index) => {
                const laborTotal = (parseFloat(item.labor_qty) || 0) * (parseFloat(item.labor_price) || 0);
                return (
                  <tr key={`labor-${index}`}>
                    <td style={styles.td}>
                      <input 
                        style={styles.smallInput}
                        type="number"
                        min="0"
                        step="0.5"
                        value={item.labor_qty || ""}
                        onChange={(e) => updateItem(index, "labor_qty", e.target.value)}
                      />
                    </td>
                    <td style={styles.td}>
                      <input 
                        style={styles.smallInput}
                        value={item.labor_description || ""}
                        onChange={(e) => updateItem(index, "labor_description", e.target.value)}
                        placeholder="Labor description"
                      />
                    </td>
                    <td style={styles.td}>
                      <input 
                        style={styles.smallInput}
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.labor_price || ""}
                        onChange={(e) => updateItem(index, "labor_price", e.target.value)}
                        placeholder="0.00"
                      />
                    </td>
                    <td style={{ ...styles.td, textAlign: "right", fontWeight: "500", fontFamily: "monospace" }}>
                      ${laborTotal.toFixed(2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ background: "#f1f5f9" }}>
                <td colSpan="2" style={styles.td}></td>
                <td style={{ ...styles.td, textAlign: "right", fontWeight: "600", fontSize: "12px" }}>Total:</td>
                <td style={{ ...styles.td, textAlign: "right", fontWeight: "700", fontFamily: "monospace" }}>
                  ${totals.laborTotal.toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Fees & Totals */}
      <div style={styles.section}>
        <div style={styles.grid3}>
          <div>
            <label style={styles.label}>Road Service Fee</label>
            <input 
              style={styles.input}
              type="number"
              min="0"
              step="0.01"
              value={invoice.road_service_fee || ""}
              onChange={(e) => setInvoice(prev => ({ ...prev, road_service_fee: e.target.value }))}
            />
          </div>
          <div>
            <label style={styles.label}>Storage Fee</label>
            <input 
              style={styles.input}
              type="number"
              min="0"
              step="0.01"
              value={invoice.storage_fee || ""}
              onChange={(e) => setInvoice(prev => ({ ...prev, storage_fee: e.target.value }))}
            />
          </div>
          <div>
            <label style={styles.label}>Tax Rate (%)</label>
            <input 
              style={styles.input}
              type="number"
              min="0"
              step="0.01"
              value={invoice.tax_rate || ""}
              onChange={(e) => setInvoice(prev => ({ ...prev, tax_rate: e.target.value }))}
            />
          </div>
        </div>

        <div style={styles.totalsBox}>
          <div style={styles.totalRow}>
            <span>Parts Total:</span>
            <span>${totals.partsTotal.toFixed(2)}</span>
          </div>
          <div style={styles.totalRow}>
            <span>Labor Total:</span>
            <span>${totals.laborTotal.toFixed(2)}</span>
          </div>
          <div style={styles.totalRow}>
            <span>Tax (Parts Only @ {invoice.tax_rate}%):</span>
            <span>${totals.taxAmount.toFixed(2)}</span>
          </div>
          <div style={styles.totalFinal}>
            <span>TOTAL DUE:</span>
            <span style={{ color: "#059669" }}>${totals.total.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div style={styles.footer}>
        <a href="/" style={{ ...styles.btnSecondary, textDecoration: "none" }}>
          Go to Full App
        </a>
        <button 
          style={{ ...styles.btnPrimary, opacity: saving ? 0.7 : 1 }}
          onClick={saveInvoice}
          disabled={saving}
        >
          {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
          {saving ? "Saving..." : "Save Invoice"}
        </button>
      </div>
    </div>
  );
}
